import { Component } from "@theme/component";
import { formatMoney } from "@theme/discount-manager";
import { createLogger } from "@theme/logger";
import { MysaEvents } from "@theme/mysa-events";
import { isHiloHandle } from "@theme/utility-shared";

/**
 * @typedef {import("@theme/discount-manager").PricingDetails} PricingDetails
 */

/**
 * @typedef {Object} BuyBoxRefs
 * @property {HTMLUListElement} [discountList] - The discount items list
 * @property {HTMLLIElement} [productLine] - Product line item
 * @property {HTMLSpanElement} [productLineTitle] - Product title span
 * @property {HTMLSpanElement} [productLineQuantity] - Quantity span
 * @property {HTMLSpanElement} [productLinePrice] - Price span
 * @property {HTMLDivElement} [collapsedPrice] - Collapsed price container
 * @property {HTMLSpanElement} [collapsedDiscountTotal] - Collapsed discount total
 * @property {HTMLSpanElement} [discountAmount] - Discount amount span
 * @property {HTMLLIElement} [sitewideItem] - Sitewide discount item
 * @property {HTMLSpanElement} [sitewidePrice] - Sitewide price span
 * @property {HTMLLIElement} [utilityRebateItem] - Utility rebate item
 * @property {HTMLSpanElement} [utilityRebatePrice] - Utility rebate price span
 * @property {HTMLLIElement} [utilityMysaItem] - Utility Mysa discount item
 * @property {HTMLSpanElement} [utilityMysaPrice] - Utility Mysa price span
 * @property {HTMLLIElement} [byobItem] - BYOB discount item
 * @property {HTMLSpanElement} [byobPrice] - BYOB price span
 * @property {HTMLLIElement} [hiloBonusItem] - Hilo bonus item
 * @property {HTMLSpanElement} [hiloBonusPrice] - Hilo bonus price span
 * @property {HTMLLIElement} [hiloFinalItem] - Hilo final cost item
 * @property {HTMLSpanElement} [hiloFinalPrice] - Hilo final price span
 * @property {HTMLLIElement} [hrsRebateItem] - ON HRS post-purchase rebate item
 * @property {HTMLSpanElement} [hrsRebatePrice] - ON HRS rebate price span
 * @property {HTMLLIElement} [hrsFinalItem] - ON HRS after-rebate total item
 * @property {HTMLSpanElement} [hrsFinalPrice] - ON HRS after-rebate price span
 */

const log = createLogger("MysaBuyBox");

const supportsSelectorHas = CSS.supports("selector(:has(*))");
const BUY_BOX_DETAILS_OPEN_CLASS = "buy-box-details-open";
const BUY_BOX_HEIGHT_PROPERTY = "--buy-box-height";

/**
 * MysaBuyBox - Custom element that handles buy box UI updates
 *
 * Listens to MysaEvents.buyboxDiscountUpdate events and updates the UI accordingly
 * @extends {Component<BuyBoxRefs>}
 */
class MysaBuyBox extends Component {
  /** @type {number} Main product discounted total in cents */
  #mainProductTotal = 0;

  /** @type {number} Main product total discount in cents (all types, for "Off" badge) */
  #mainProductDiscount = 0;

  /** @type {number} Main product general/sitewide discount in cents */
  #mainProductGeneralDiscount = 0;

  /** @type {number} Sum of all add-on selling prices in cents */
  #addonTotal = 0;

  /** @type {number} Sum of all add-on discount savings in cents (accumulated into sitewide line) */
  #addonDiscount = 0;

  /** @type {Array<{variantId: string, title: string, price: number, compareAtPrice: number, quantity: number}>} */
  #addons = [];

  /** @type {number} Current ON HRS rebate amount in cents (first unit only) */
  #hrsRebateCents = 0;

  /**
   * ON HRS (Save on Energy) post-purchase rebate: fixed mail-in rebate
   * amount in cents (0 = not eligible). Read live from
   * `data-hrs-rebate-amount-cents` so utility-driven section morphs that
   * update the attribute take effect without remounting.
   * @returns {number}
   */
  get #hrsRebateAmountCents() {
    return parseInt(this.dataset.hrsRebateAmountCents ?? "", 10) || 0;
  }

  constructor() {
    super();
    this.handleDiscountUpdate = this.#onDiscountUpdate.bind(this);
    this.handleAddonUpdate = this.#onAddonUpdate.bind(this);
  }

  /**
   * Fallback for browsers without CSS :has() — toggle body class when pricing details open.
   * @param {Event} event
   */
  #onDetailsToggle = (event) => {
    if (supportsSelectorHas) return;

    const target = event.target;
    if (
      !(target instanceof HTMLDetailsElement) ||
      !target.classList.contains("bb-details")
    ) {
      return;
    }

    document.body.classList.toggle(BUY_BOX_DETAILS_OPEN_CLASS, target.open);
  };

  #bindGoogleWidgetHideFallback() {
    if (supportsSelectorHas) return;

    this.addEventListener("toggle", this.#onDetailsToggle);

    const details = this.querySelector(".bb-details");
    if (details?.open) {
      document.body.classList.add(BUY_BOX_DETAILS_OPEN_CLASS);
    }
  }

  #unbindGoogleWidgetHideFallback() {
    if (supportsSelectorHas) return;

    this.removeEventListener("toggle", this.#onDetailsToggle);
    document.body.classList.remove(BUY_BOX_DETAILS_OPEN_CLASS);
  }

  /** @type {ResizeObserver | null} */
  #heightObserver = null;

  /**
   * Publish the bar's resting height so the page can reserve space for it and
   * keep scroll targets clear. The Liquid `--buy-box-height` fallback is a sum
   * of fixed line heights; the real bar is taller than that estimate, and
   * taller again when a localized string wraps onto a second line.
   *
   * The expanded pricing panel is transient chrome, not resting height —
   * reserving for it would resize the document every time a shopper toggles it.
   */
  #publishHeight = () => {
    if (this.querySelector(".bb-details[open]")) return;

    const { height } = this.getBoundingClientRect();
    if (height <= 0) return;

    document.documentElement.style.setProperty(
      BUY_BOX_HEIGHT_PROPERTY,
      `${Math.ceil(height)}px`,
    );
  };

  #observeHeight() {
    this.#publishHeight();
    this.#heightObserver = new ResizeObserver(this.#publishHeight);
    this.#heightObserver.observe(this);
  }

  // The published height is left in place: every rule that reads it is already
  // gated on `:has(.buy-box-wrapper)`, so a stale value applies to nothing, and
  // clearing it here would fight a section re-render for whichever of the old
  // and new bar runs its callback last.
  #unobserveHeight() {
    this.#heightObserver?.disconnect();
    this.#heightObserver = null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#syncFromDataset();
    this.#bindGoogleWidgetHideFallback();
    this.#observeHeight();

    document.addEventListener(
      MysaEvents.buyboxDiscountUpdate,
      this.handleDiscountUpdate,
    );
    document.addEventListener(
      MysaEvents.complementaryProductsUpdate,
      this.handleAddonUpdate,
    );
  }

  /**
   * Section morphs (utility selection, variant changes) refresh
   * `data-*` on this element and the inner DOM, but the custom element
   * stays connected — so the cached pricing fields read in
   * `connectedCallback` would otherwise stay at pre-morph values and a
   * subsequent `#onAddonUpdate` would recompute the collapsed total /
   * "Off" badge from stale data, clobbering the freshly-morphed DOM.
   * Resync from the new dataset before the next event-driven update.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#syncFromDataset();
  }

  /**
   * Read the server-rendered pricing snapshot off `data-*` into the
   * cached instance fields. Source of truth for the collapsed UI
   * between `buyboxDiscountUpdate` events.
   */
  #syncFromDataset() {
    this.#mainProductTotal =
      parseInt(this.dataset.checkoutPriceCents ?? "", 10) || 0;
    this.#mainProductDiscount =
      parseInt(this.dataset.totalDiscountCents ?? "", 10) || 0;
    this.#mainProductGeneralDiscount =
      parseInt(this.dataset.generalDiscountCents ?? "", 10) || 0;
    this.#hrsRebateCents = parseInt(this.dataset.hrsRebateCents ?? "", 10) || 0;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindGoogleWidgetHideFallback();
    this.#unobserveHeight();
    document.removeEventListener(
      MysaEvents.buyboxDiscountUpdate,
      this.handleDiscountUpdate,
    );
    document.removeEventListener(
      MysaEvents.complementaryProductsUpdate,
      this.handleAddonUpdate,
    );
  }

  /**
   * Handle discount update events
   * @param {Event} event - The discount update event
   */
  #onDiscountUpdate(event) {
    const customEvent = /** @type {CustomEvent<PricingDetails>} */ (event);
    const pricing = customEvent.detail;

    // Utility bundle: discount lines are static from Liquid, only update checkout price and quantity
    if (pricing.isUtilityBundle) {
      const checkoutPrice = pricing.discountedTotal;
      this.#mainProductTotal = checkoutPrice;
      this.#updateGrandTotal();
      if (this.refs.productLineQuantity) {
        this.refs.productLineQuantity.textContent = String(pricing.quantity);
      }
      if (this.refs.productLinePrice) {
        this.refs.productLinePrice.textContent = formatMoney(
          pricing.originalTotal || checkoutPrice,
        );
      }
      return;
    }

    log(`#onDiscountUpdate - discounts:`, pricing.discounts);
    this.#updateUI(pricing);
  }

  /**
   * Update the buy box UI with new discount data
   * @param {PricingDetails} pricing - Pricing details from DiscountManager
   */
  #updateUI(pricing) {
    // DiscountManager already calculates discountedTotal correctly for all cases
    const checkoutPrice = pricing.discountedTotal;
    this.#mainProductTotal = checkoutPrice;

    this.#mainProductDiscount = pricing.totalDiscount || 0;
    this.#mainProductGeneralDiscount = pricing.generalDiscount || 0;
    this.#hrsRebateCents = this.#calculateHrsRebate(pricing);

    this.#updateGrandTotal();
    this.#updateDiscountBadge();

    // Get product title from data attribute
    const productTitle = this.dataset.productTitle || "Product";

    // Update product line: Update quantity and price separately
    if (this.refs.productLineTitle) {
      this.refs.productLineTitle.textContent = productTitle;
    }
    if (this.refs.productLineQuantity) {
      this.refs.productLineQuantity.textContent = String(pricing.quantity);
    }
    if (this.refs.productLinePrice) {
      this.refs.productLinePrice.textContent = formatMoney(
        pricing.originalTotal || checkoutPrice,
      );
    }

    if (!this.refs.discountList) {
      return;
    }

    // Update server-rendered discount items (toggle visibility + update amounts)
    this.#updateDiscountItems(pricing);
  }

  /**
   * Handle complementary product (add-on) quantity changes
   * @param {Event} event
   */
  #onAddonUpdate(event) {
    const { addons } =
      /** @type {CustomEvent<{addons: Array<{variantId: string, title: string, price: number, compareAtPrice: number, quantity: number}>}>} */ (
        event
      ).detail;

    this.#addons = addons;
    this.#addonTotal = addons.reduce((sum, a) => sum + a.price * a.quantity, 0);
    this.#addonDiscount = addons.reduce((sum, a) => {
      if (a.compareAtPrice > 0) {
        return sum + (a.compareAtPrice - a.price) * a.quantity;
      }
      return sum;
    }, 0);

    this.#renderAddons();
    this.#updateSitewideDiscount();
    this.#updateGrandTotal();
    this.#updateDiscountBadge();
  }

  /**
   * Render add-on line items in the pricing breakdown at their selling price.
   * Discount savings are accumulated into the sitewide discount line instead.
   */
  #renderAddons() {
    for (const el of this.querySelectorAll(".bb-line--addon")) {
      el.remove();
    }

    if (!this.refs.discountList || this.#addons.length === 0) return;

    for (const addon of this.#addons) {
      const li = document.createElement("li");
      li.className = "bb-line bb-line--addon";

      const label = document.createElement("span");
      label.className = "bb-line__label";

      const titleSpan = document.createElement("span");
      titleSpan.textContent = addon.title;

      const sep = document.createElement("span");
      sep.textContent = " \u00D7 ";

      const qtySpan = document.createElement("span");
      qtySpan.textContent = String(addon.quantity);

      label.append(titleSpan, sep, qtySpan);

      const price = document.createElement("span");
      price.className = "bb-line__price";
      price.textContent = formatMoney(addon.price * addon.quantity);

      li.append(label, price);
      this.refs.discountList.appendChild(li);
    }
  }

  /**
   * Update the collapsed summary price to include main product + add-ons,
   * minus the ON HRS post-purchase rebate (effective total after rebate)
   */
  #updateGrandTotal() {
    const grandTotal = Math.max(
      0,
      this.#mainProductTotal + this.#addonTotal - this.#hrsRebateCents,
    );

    const container = this.refs.collapsedPrice;

    if (!container) return;

    const priceElement = container.querySelector(".bb-line__price");
    if (priceElement) {
      priceElement.textContent = formatMoney(grandTotal);
    }
  }

  /**
   * Update the "Off" discount badge to include main product, add-on,
   * and ON HRS post-purchase rebate amounts
   */
  #updateDiscountBadge() {
    const totalOff =
      this.#mainProductDiscount + this.#addonDiscount + this.#hrsRebateCents;
    this.#updateDiscountItem(
      this.refs.collapsedDiscountTotal,
      this.refs.discountAmount,
      totalOff,
      false,
    );
  }

  /**
   * Update sitewide/general discount line with accumulated add-on discounts.
   * When add-ons share the same general offer, their savings fold into
   * the existing sitewide line rather than appearing as separate entries.
   */
  #updateSitewideDiscount() {
    const accumulated = this.#mainProductGeneralDiscount + this.#addonDiscount;
    this.#updateDiscountItem(
      this.refs.sitewideItem,
      this.refs.sitewidePrice,
      accumulated,
    );
  }

  /** @type {Array<{item: keyof BuyBoxRefs, price: keyof BuyBoxRefs, value: keyof PricingDetails}>} */
  static #discountMap = [
    {
      item: "utilityRebateItem",
      price: "utilityRebatePrice",
      value: "utilityRebate",
    },
    {
      item: "utilityMysaItem",
      price: "utilityMysaPrice",
      value: "utilityMysa",
    },
    { item: "byobItem", price: "byobPrice", value: "byobDiscount" },
  ];

  /**
   * Update server-rendered discount items (toggle visibility + update amounts)
   * Pattern: Liquid renders all items, JS toggles hidden and updates text
   * @param {PricingDetails} pricing - Pricing details from DiscountManager
   */
  #updateDiscountItems(pricing) {
    // Sitewide: accumulate main product general discount + add-on discounts
    this.#updateSitewideDiscount();

    // Update remaining discount items
    for (const { item, price, value } of MysaBuyBox.#discountMap) {
      this.#updateDiscountItem(
        /** @type {HTMLElement | undefined} */ (this.refs[item]),
        /** @type {HTMLElement | undefined} */ (this.refs[price]),
        /** @type {number} */ (pricing[value]) || 0,
      );
    }

    // ON HRS post-purchase rebate UI (Save on Energy)
    this.#updateHrsPostPurchase(pricing);

    // Hilo bonus UI (post-purchase amount covered by Hilo program)
    // Hilo structure: rebate at checkout ($49/$29), Hilo refunds the rest post-purchase
    // - Eligible units (≤maxQty): pay rebate at checkout, Hilo refunds remaining → net $0
    // - Excess units (>maxQty): no Hilo discount, pay full price
    const isHilo = isHiloHandle(pricing.utilityProviderCode);
    if (!isHilo) {
      return;
    }

    const unitPrice = pricing.originalPrice || 0;
    const maxQty = pricing.utilityMaxQuantity || 0;
    const eligibleQty =
      maxQty > 0 ? Math.min(pricing.quantity, maxQty) : pricing.quantity;

    // Hilo bonus = (unit price - rebate per unit) × eligible qty
    // This is what Hilo refunds post-purchase (checkout price for eligible units)
    // utilityRebate is already capped to eligible qty by discount-manager
    const rebatePerUnit =
      eligibleQty > 0 ? (pricing.utilityRebate || 0) / eligibleQty : 0;
    const hiloBonusTotal = (unitPrice - rebatePerUnit) * eligibleQty;

    // Update Hilo bonus amount
    this.#updateDiscountItem(
      this.refs.hiloBonusItem,
      this.refs.hiloBonusPrice,
      hiloBonusTotal,
    );

    // Update Hilo final cost - always visible to show post-enrollment cost
    // When qty <= maxQty: shows $0 (Hilo covers everything)
    // When qty > maxQty: shows cost for excess units at full price
    if (!this.refs.hiloFinalPrice) return;

    const excessQty = maxQty > 0 ? Math.max(0, pricing.quantity - maxQty) : 0;
    const hiloFinalCost = excessQty * unitPrice;
    this.refs.hiloFinalPrice.textContent = formatMoney(hiloFinalCost);
  }

  /**
   * Calculate the ON HRS post-purchase rebate amount (Save on Energy)
   * Fixed $88 mail-in rebate on the first unit. After-rebate price is
   * derived from checkout minus this amount so stackable promos apply.
   *
   * If a BYOB-eligible unit is already in the cart, that unit consumes the
   * reserved rebate slot (mirrors `pageReserved` math in discount-manager).
   * No page unit is reserved, so the rebate must not be subtracted again.
   * @param {PricingDetails} pricing - Pricing details from DiscountManager
   * @returns {number} Rebate amount in cents
   */
  #calculateHrsRebate(pricing) {
    if (!this.#hrsRebateAmountCents) return 0;

    // The HRS reserved slot is per-product (smart-thermostat), not aggregate
    // BYOB. Counting all BYOB-eligible cart lines here would suppress the
    // rebate when an unrelated eligible product (e.g. baseboard) sits in
    // cart — gate on the same-product cart count instead.
    const reserved = pricing.byobReservedQuantity || 0;
    const slotCartQty = pricing.byobReservedSlotCartQuantity || 0;
    const pageReserved = Math.max(0, reserved - slotCartQty);
    if (reserved > 0 && pageReserved === 0) return 0;

    return this.#hrsRebateAmountCents;
  }

  /**
   * ON HRS post-purchase rebate UI (Save on Energy)
   * Updates the rebate line and the after-rebate total line.
   * The rebate applies to one unit only; the final line shows the full
   * order checkout minus that single rebate so the total scales with qty.
   * Both lines are hidden when the rebate is 0 (e.g. cart already consumed
   * the reserved slot) since the after-rebate total would equal the regular
   * checkout price and add no information.
   * @param {PricingDetails} pricing - Pricing details from DiscountManager
   */
  #updateHrsPostPurchase(pricing) {
    if (!this.#hrsRebateAmountCents) return;

    this.#updateDiscountItem(
      this.refs.hrsRebateItem,
      this.refs.hrsRebatePrice,
      this.#hrsRebateCents,
    );

    if (this.refs.hrsFinalItem) {
      if (this.#hrsRebateCents > 0) {
        this.refs.hrsFinalItem.removeAttribute("hidden");
      } else {
        this.refs.hrsFinalItem.setAttribute("hidden", "");
      }
    }

    if (!this.refs.hrsFinalPrice) return;
    this.refs.hrsFinalPrice.textContent = formatMoney(
      Math.max(0, pricing.discountedTotal - this.#hrsRebateCents),
    );
  }

  /**
   * Helper to update a discount item's visibility and price
   * @param {HTMLElement | undefined} item - The container element to show/hide
   * @param {HTMLElement | undefined} priceSpan - The price span element to update
   * @param {number} amount - The amount in cents
   * @param {boolean} [showNegative=true] - Whether to prefix with "-"
   */
  #updateDiscountItem(item, priceSpan, amount, showNegative = true) {
    if (!item) return;

    if (amount > 0) {
      if (priceSpan) {
        const formatted = formatMoney(amount);
        priceSpan.textContent = showNegative ? `-${formatted}` : formatted;
      }
      item.removeAttribute("hidden");
    } else {
      item.setAttribute("hidden", "");
    }
  }
}

// Register custom elements
if (!customElements.get("mysa-buy-box")) {
  customElements.define("mysa-buy-box", MysaBuyBox);
}
